<template>
  <div id="payment-element" class="payment-element w-100"></div>
</template>

<script>
import {loadStripe} from '@stripe/stripe-js'

export default {
  data() {
    return {
      stripe: null,
      elements: null,
      paymentElement: null,
      paymentIntentId: null,
      amount: 0,
      currency: 'usd',
      paymentMethodConfiguration: null,
      captureMethod: 'manual',
    }
  },

  async mounted() {
    try {
      let publishableKey = _.get(apr, 'stripe.publishable_key');
      this.stripe = await loadStripe(publishableKey);

      this.amount = _.get(apr, 'stripe.amount', 0);
      this.currency = (_.get(apr, 'stripe.currency') || 'usd').toLowerCase();
      this.paymentMethodConfiguration = _.get(apr, 'stripe.payment_method_configuration', null);
      this.captureMethod = _.get(apr, 'stripe.capture_method', 'manual');

      this.initElements();
      this.setPaymentElementListener();
      this.setEventListeners();
    } catch (e) {
      console.error(e);
    }
  },

  methods: {
    initElements() {
      if (!this.stripe) {
        return;
      }

      const options = {
        mode: 'payment',
        amount: this.amount || 0,
        currency: (this.currency || 'usd').toLowerCase(),
        locale: 'en',
        captureMethod: this.captureMethod || 'manual',
      };

      if (this.paymentMethodConfiguration) {
        options.paymentMethodConfiguration = this.paymentMethodConfiguration;
      }

      if (this.elements) {
        this.elements.update(options);

        return;
      }

      this.elements = this.stripe.elements(options);
      this.paymentElement = this.elements.create('payment');
      this.paymentElement.mount('#payment-element');
    },

    setPaymentElementListener() {
      // Listen to changes in the Payment Element
      this.paymentElement.on('change', (event) => {
        if (event.complete) {
          console.log("Payment method is selected and valid");
          this.$emit('update-submit-disabled-state', false);

          return;
        }

        console.log("Payment method not ready yet");
        this.$emit('update-submit-disabled-state', true);
      });
    },

    setEventListeners() {
      this.$root.$on(`checkout::reservation-submitted`, async () => {
        const {error: submitError} = await this.elements.submit();
        if (submitError) {
          if (this.isApplePaySubmitError(submitError)) {
            this.$root.$emit('payment-container::verification-failed');

            return;
          }

          console.error('Submit error:', submitError.message);
          this.$root.$emit('payment-container::verification-failed', submitError.message);

          return;
        }

        const {confirmationToken, error} = await this.stripe.createConfirmationToken({
          elements: this.elements,
          locale: 'en',
        });

        if (error) {
          this.$root.$emit('fatal-error', 'Failed to create confirmation token:', error.message);
          console.log('Failed to create confirmation token: ' + error.message);
        } else {
          this.$root.$emit('gateway-changed', {
            gateway: 'stripe',
            gateway_data: {
              'confirmation_token': confirmationToken.id,
              'payment_intent_id': this.paymentIntentId,
            },
          });

          this.$root.$emit('checkout::credit-card-was-validated');
          console.log('Got confirmation token:', confirmationToken.id);
        }
      });

      this.$root.$on(`checkout::additional-actions-required`, async (data) => {
        // Stripe SDK scenario
        if (_.get(data, 'type') === 'use_stripe_sdk') {
          this.paymentIntentId = _.get(data, 'payment_intent_id', this.paymentIntentId);
          let clientSecret = _.get(data, 'client_secret');
          const nextActionOptions = {
            clientSecret,
            locale: 'en',
          };

          const waitUrl = this.waitUrl();
          if (waitUrl) {
            nextActionOptions.return_url = waitUrl;
          }

          const {error} = await this.stripe.handleNextAction(nextActionOptions);

          if (error) {
            console.error('Validation error: ' + _.get(error, 'code'));
            this.$root.$emit('payment-container::verification-failed', this.messageEnByCode(error));
          } else {
            window.location.href = `/stripe-payment/wait/${this.paymentIntentId}`;
          }
        }

        // Redirect scenario
        if (_.get(data, 'type') === 'redirect_to_url') {
          this.paymentIntentId = _.get(data, 'payment_intent_id', this.paymentIntentId);
          window.location.href = _.get(data, 'redirect_to_url');
        }

      });

      this.$root.$on(`checkout::payment-intent-updated`, async (data) => {
        this.amount = _.get(data, 'amount', this.amount);
        this.currency = (_.get(data, 'currency', this.currency) || 'usd').toLowerCase();
        this.paymentMethodConfiguration = _.get(data, 'payment_method_configuration', this.paymentMethodConfiguration);
        this.captureMethod = _.get(data, 'capture_method', this.captureMethod);
        this.paymentIntentId = null;

        this.initElements();

        console.log('Payment element configuration was updated');
      });
    },

    isApplePaySubmitError(error) {
      return _.get(error, 'type') === 'validation_error' && _.get(error, 'code') === 'incomplete';
    },

    waitUrl() {
      if (!this.paymentIntentId) {
        return null;
      }

      return `/stripe-payment/wait/${this.paymentIntentId}`;
    },

    messageEnByCode(error) {
      let errorCode = _.get(error, 'code')

      const messages = {
        authentication_error: 'Authentication failed. Please try another payment method.',
        payment_intent_authentication_failure: '3-D Secure authentication failed.',
        payment_intent_unexpected_state: 'Payment is not in a state that can be confirmed.',
        card_declined: 'Your card was declined.',
        insufficient_funds: 'Insufficient funds.',
        expired_card: 'Your card has expired.',
        incorrect_cvc: 'Incorrect CVC.',
        processing_error: 'An error occurred while processing the card.',
        invalid_request_error: 'Invalid payment request. Please try again.',
      };

      return _.get(messages, errorCode, 'Verification failed. Please try another card or payment method.')
    }
  },
}
</script>
